import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { fetchCategories, fetchTopics, type Category, type Topic } from '../../features/topics/api';

export function ForumPage() {
  const { user } = useAuth();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [topicsData, categoriesData] = await Promise.all([
        fetchTopics(100, true), // Request topics with statistics
        fetchCategories(),
      ]);
      setTopics(topicsData);
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function loadTopics() {
    try {
      const data = await fetchTopics(50, true); // Request topics with statistics
      setTopics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load topics');
    }
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-slate-600 dark:text-slate-400 text-lg">Loading topics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400">
          Error: {error}
        </div>
        <Button onClick={loadTopics} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-bold text-slate-900 dark:text-white">Forum Topics</h1>
        {user?.role === 'admin' && (
          <Button onClick={() => setShowCreateForm(true)}>
            Create Topic
          </Button>
        )}
      </div>

      {/* Create Topic Modal */}
      {user?.role === 'admin' && (
        <Modal
          isOpen={showCreateForm}
          onClose={() => setShowCreateForm(false)}
          title="Create New Topic"
          size="lg"
        >
          <CreateTopicForm
            onSuccess={() => {
              setShowCreateForm(false);
              loadTopics();
            }}
            onCancel={() => setShowCreateForm(false)}
          />
        </Modal>
      )}

      {(!topics || topics.length === 0) ? (
        <div className="bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-xl p-12 text-center">
          <p className="text-slate-600 dark:text-slate-400 text-lg mb-4">No topics yet. Be the first to create one!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Group topics by category */}
          {(categories || []).map((category) => {
            const categoryTopics = topics.filter((t) => t.categoryId === category.id);
            if (categoryTopics.length === 0) return null;

            return (
              <div key={category.id} className="bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-xl overflow-hidden shadow-sm">
                {/* Category Header */}
                <div className="bg-gradient-to-r from-brand-50 to-accent-50 dark:from-brand-950/30 dark:to-accent-950/30 border-b border-slate-200 dark:border-dark-border px-6 py-4">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">{category.name}</h2>
                  {category.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{category.description}</p>
                  )}
                </div>

                {/* Topics in this category */}
                <div className="divide-y divide-slate-200 dark:divide-dark-border">
                  {categoryTopics.map((topic) => (
                    <div
                      key={topic.id}
                      onClick={() => navigate(`/forum/topics/${topic.slug}`)}
                      className="p-5 hover:bg-slate-50 dark:hover:bg-dark-hover cursor-pointer transition-all group"
                    >
                      <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0 w-full">
                          <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors line-clamp-2 sm:line-clamp-1">{topic.title}</h3>
                          {topic.description && (
                            <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2">{topic.description}</p>
                          )}
                        </div>

                        {/* Statistics */}
                        {topic.stats && (
                          <div className="flex-shrink-0 space-y-2 text-sm w-full sm:w-auto sm:min-w-[280px] text-left sm:text-right mt-3 sm:mt-0">
                            <div className="flex items-center justify-start sm:justify-end gap-3 text-slate-600 dark:text-slate-400">
                              <span title="Total threads">💬 {topic.stats.totalThreads + topic.stats.totalSubthreads}</span>
                              <span title="Total posts">📝 {topic.stats.totalPosts}</span>
                              <span title="Total comments">💭 {topic.stats.totalComments}</span>
                            </div>
                            {topic.stats.lastThreadTitle && topic.stats.lastPostTime && (
                              <div className="text-xs text-slate-500 dark:text-slate-500 sm:text-right">
                                <div className="font-medium truncate" title={topic.stats.lastThreadTitle}>
                                  Last: {topic.stats.lastThreadTitle}
                                </div>
                                <div className="mt-1">
                                  <span>{new Date(topic.stats.lastPostTime).toLocaleDateString()}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Uncategorized topics */}
          {(() => {
            const uncategorizedTopics = topics.filter((t) => !t.categoryId);
            if (uncategorizedTopics.length === 0) return null;

            return (
              <div className="bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-border rounded-xl overflow-hidden shadow-sm">
                <div className="bg-gradient-to-r from-slate-100 to-slate-50 dark:from-dark-bg-secondary dark:to-dark-card border-b border-slate-200 dark:border-dark-border px-6 py-4">
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Uncategorized</h2>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Topics without a category</p>
                </div>

                <div className="divide-y divide-slate-200 dark:divide-dark-border">
                  {uncategorizedTopics.map((topic) => (
                    <div
                      key={topic.id}
                      onClick={() => navigate(`/forum/topics/${topic.slug}`)}
                      className="p-5 hover:bg-slate-50 dark:hover:bg-dark-hover cursor-pointer transition-all group"
                    >
                      <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0 w-full">
                          <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors line-clamp-2 sm:line-clamp-1">{topic.title}</h3>
                          {topic.description && (
                            <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2">{topic.description}</p>
                          )}
                        </div>

                        {/* Statistics */}
                        {topic.stats && (
                          <div className="flex-shrink-0 space-y-2 text-sm w-full sm:w-auto sm:min-w-[280px] text-left sm:text-right mt-3 sm:mt-0">
                            <div className="flex items-center justify-start sm:justify-end gap-3 text-slate-600 dark:text-slate-400">
                              <span title="Total threads">💬 {topic.stats.totalThreads + topic.stats.totalSubthreads}</span>
                              <span title="Total posts">📝 {topic.stats.totalPosts}</span>
                              <span title="Total comments">💭 {topic.stats.totalComments}</span>
                            </div>
                            {topic.stats.lastThreadTitle && topic.stats.lastPostTime && (
                              <div className="text-xs text-slate-500 dark:text-slate-500 sm:text-right">
                                <div className="font-medium truncate" title={topic.stats.lastThreadTitle}>
                                  Last: {topic.stats.lastThreadTitle}
                                </div>
                                <div className="mt-1">
                                  <span>{new Date(topic.stats.lastPostTime).toLocaleDateString()}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// Helper function to generate URL-friendly slug from title
function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')      // Replace spaces with hyphens
    .replace(/-+/g, '-')       // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '');    // Remove leading/trailing hyphens
}

function CreateTopicForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load categories on mount
  useEffect(() => {
    async function loadCategories() {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    }
    loadCategories();
  }, []);

  // Auto-populate slug when title changes (but allow manual override)
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    // Only auto-generate if slug hasn't been manually edited
    // (or if slug matches the auto-generated version of previous title)
    if (!slug || slug === generateSlug(title)) {
      setSlug(generateSlug(newTitle));
    }
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !slug.trim()) {
      setError('Title and slug are required');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch('/api/forum/topics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, slug, description, categoryId: categoryId || undefined }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create topic');
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create topic');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded-lg p-4 text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Title *
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
          placeholder="e.g., General Discussion"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Slug * <span className="text-slate-500 dark:text-slate-400 font-normal">(auto-generated, editable for i18n)</span>
        </label>
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
          className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-slate-100 font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
          placeholder="e.g., general-discussion or general-discussion-zh"
          required
        />
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1.5">
          💡 Auto-populated from title. Edit for multi-language support (e.g., add -zh, -en suffix)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
          rows={3}
          placeholder="Optional description of this topic..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Category <span className="text-slate-500 dark:text-slate-400 font-normal">(optional)</span>
        </label>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
        >
          <option value="">-- No Category --</option>
          {(categories || []).map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-dark-border">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Creating...' : 'Create Topic'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
